import React, { useEffect, useState } from 'react';
import apiService from '../../../services/ApiService';
import EditorChrome, { type EditorChromeVariant } from '../../ui/EditorChrome';
import Button from '../../ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import type { BuildProfile, BuildPurpose } from '../../../types/buildExpansion';

/**
 * Owner editor for the build profile: purposes, stage, summary, weight,
 * stat-strip preferences, restrained customization (approved accent palette,
 * hero options), and the optional owner card. All enums mirror the backend
 * canonical lists in build_profile_utils.php.
 */

const PURPOSES: BuildPurpose[] = [
  'street', 'daily', 'track', 'time_attack', 'drag', 'drift', 'rally', 'autocross',
  'show', 'stance', 'off_road', 'overland', 'restoration', 'oem_plus', 'touring',
  'competition', 'mixed_use',
];

const PURPOSE_LABELS: Record<BuildPurpose, string> = {
  street: 'Street',
  daily: 'Daily',
  track: 'Track',
  time_attack: 'Time attack',
  drag: 'Drag',
  drift: 'Drift',
  rally: 'Rally',
  autocross: 'Autocross',
  show: 'Show',
  stance: 'Stance',
  off_road: 'Off-road',
  overland: 'Overland',
  restoration: 'Restoration',
  oem_plus: 'OEM+',
  touring: 'Touring',
  competition: 'Competition',
  mixed_use: 'Mixed use',
};

const PURPOSE_HINTS: Record<BuildPurpose, string> = {
  street: 'Road manners',
  daily: 'Commute ready',
  track: 'Circuit work',
  time_attack: 'Hot laps',
  drag: 'Straight line',
  drift: 'Sideways',
  rally: 'Dirt and gravel',
  autocross: 'Cone courses',
  show: 'Display ready',
  stance: 'Fitment first',
  off_road: 'Trail capable',
  overland: 'Adventure travel',
  restoration: 'Bring-back build',
  oem_plus: 'Factory-plus',
  touring: 'Long distance',
  competition: 'Race class',
  mixed_use: 'Multi-role',
};

type PreferenceToggleProps = {
  label: string;
  hint: string;
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
};

function PreferenceToggle({ label, hint, pressed, disabled, onClick }: PreferenceToggleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      title={hint}
      className={`min-h-[52px] min-w-0 w-full px-2.5 py-2 rounded-xl border text-left transition-[transform,colors,background-color,border-color] duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 ${
        pressed
          ? 'bg-orange-500/15 border-orange-500/45 text-orange-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
          : 'bg-[#0a0a0a]/80 border-white/[0.08] text-gray-300 hover:border-white/[0.16] hover:bg-white/[0.03]'
      }`}
    >
      <span className="block text-sm font-medium leading-snug break-words">{label}</span>
      <span
        className={`block text-[11px] mt-0.5 leading-snug break-words ${
          pressed ? 'text-orange-200/70' : 'text-gray-500'
        }`}
      >
        {hint}
      </span>
    </button>
  );
}

/** buildStages() in build_profile_utils.php */
const BUILD_STAGES = [
  { value: 'planning', label: 'Planning' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'rebuild', label: 'Rebuild' },
];

/** buildAccentPalette() in build_profile_utils.php; all pass WCAG AA on gray-950. */
const ACCENT_PALETTE = ['orange', 'red', 'amber', 'emerald', 'sky', 'violet', 'rose', 'slate'];
const ACCENT_SWATCH_CLASSES: Record<string, string> = {
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  sky: 'bg-sky-500',
  violet: 'bg-violet-500',
  rose: 'bg-rose-500',
  slate: 'bg-slate-500',
};

/** buildHeroPlacements() in build_profile_utils.php */
const HERO_PLACEMENTS = [
  { value: 'bottom_left', label: 'Bottom left' },
  { value: 'bottom_center', label: 'Bottom center' },
  { value: 'top_left', label: 'Top left' },
];

const HERO_TREATMENTS = [
  { value: 'light', label: 'Light text' },
  { value: 'dark', label: 'Dark text' },
];

/**
 * Stats strip chips. Power/torque are edited above and always shown when set,
 * so they are intentionally omitted here (also filtered out of saved prefs).
 * Keys align with buildStatKeys() in build_profile_utils.php.
 */
const STAT_OPTIONS: { key: string; label: string; hint: string }[] = [
  { key: 'weight', label: 'Weight', hint: 'Curb / scale weight' },
  { key: 'power_to_weight', label: 'Power to weight', hint: 'HP per lb' },
  { key: 'modification_count', label: 'Mods', hint: 'Parts on this build' },
  { key: 'documentation_score', label: 'Docs score', hint: 'Completeness' },
  { key: 'media_count', label: 'Media', hint: 'Photos and clips' },
  { key: 'event_count', label: 'Events', hint: 'Shows attended' },
  { key: 'award_count', label: 'Awards', hint: 'Trophies and placings' },
  { key: 'best_performance', label: 'Best result', hint: 'Top timed run' },
  { key: 'years_documented', label: 'Years', hint: 'Time documented' },
  { key: 'view_count', label: 'Views', hint: 'Page views' },
  { key: 'favorite_count', label: 'Favorites', hint: 'Saves by others' },
];

const STAT_OPTION_KEYS = new Set(STAT_OPTIONS.map((s) => s.key));

const MAX_SECONDARY_PURPOSES = 3;
const MAX_STAT_PREFS = 8;

function sanitizeStatPreferences(keys: string[]): string[] {
  return keys.filter((k) => STAT_OPTION_KEYS.has(k)).slice(0, MAX_STAT_PREFS);
}

interface FormState {
  purpose_primary: string;
  purposes: BuildPurpose[];
  build_stage: string;
  current_goal: string;
  summary: string;
  weight_lbs: string;
  weight_source: 'owner' | 'scale' | 'factory';
  accent_color: string;
  hero_text_placement: string;
  hero_treatment: string;
  stat_preferences: string[];
  owner_card_enabled: boolean;
  owner_display_name: string;
  owner_bio: string;
  owner_location: string;
  owner_role: string;
  owner_ownership_since: string;
}

const emptyForm: FormState = {
  purpose_primary: '',
  purposes: [],
  build_stage: '',
  current_goal: '',
  summary: '',
  weight_lbs: '',
  weight_source: 'owner',
  accent_color: '',
  hero_text_placement: '',
  hero_treatment: '',
  stat_preferences: [],
  owner_card_enabled: false,
  owner_display_name: '',
  owner_bio: '',
  owner_location: '',
  owner_role: '',
  owner_ownership_since: '',
};

const formFromProfile = (profile: BuildProfile): FormState => ({
  purpose_primary: profile.purpose_primary ?? '',
  purposes: profile.purposes.slice(0, MAX_SECONDARY_PURPOSES),
  build_stage: profile.build_stage ?? '',
  current_goal: profile.current_goal ?? '',
  summary: profile.summary ?? '',
  weight_lbs: profile.weight_lbs != null ? String(profile.weight_lbs) : '',
  weight_source: profile.weight_source ?? 'owner',
  accent_color: profile.accent_color ?? '',
  hero_text_placement: profile.hero_text_placement ?? '',
  hero_treatment: profile.hero_treatment ?? '',
  stat_preferences: sanitizeStatPreferences(profile.stat_preferences),
  owner_card_enabled: profile.owner_card_enabled,
  owner_display_name: typeof profile.owner_card.display_name === 'string' ? profile.owner_card.display_name : '',
  owner_bio: typeof profile.owner_card.bio === 'string' ? profile.owner_card.bio : '',
  owner_location: typeof profile.owner_card.location === 'string' ? profile.owner_card.location : '',
  owner_role: typeof profile.owner_card.role === 'string' ? profile.owner_card.role : '',
  owner_ownership_since:
    typeof profile.owner_card.ownership_since === 'string'
      ? profile.owner_card.ownership_since
      : '',
});

/** Map the backend's single error message onto the closest field. */
const fieldForBackendError = (message: string): keyof FormState | null => {
  const lower = message.toLowerCase();
  if (lower.includes('weight')) return 'weight_lbs';
  if (lower.includes('purpose')) return 'purpose_primary';
  if (lower.includes('stage')) return 'build_stage';
  if (lower.includes('accent')) return 'accent_color';
  if (lower.includes('placement')) return 'hero_text_placement';
  if (lower.includes('treatment')) return 'hero_treatment';
  return null;
};

export interface BuildProfileEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: number;
  /** Receives the saved profile returned by the backend. */
  onSaved?: (profile: BuildProfile | null) => void;
  /** `panel` embeds the form inside Manage build without a nested dialog. */
  variant?: EditorChromeVariant;
}

export const BuildProfileEditorModal: React.FC<BuildProfileEditorModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  onSaved,
  variant = 'modal',
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setErrors({});
    setGeneralError('');
    apiService
      .getBuildProfile(vehicleId)
      .then(({ profile }) => {
        if (cancelled) return;
        setForm(profile ? formFromProfile(profile) : emptyForm);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load the build profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, vehicleId]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const toggleSecondaryPurpose = (purpose: BuildPurpose) => {
    setForm((prev) => {
      const active = prev.purposes.includes(purpose);
      if (active) {
        return { ...prev, purposes: prev.purposes.filter((p) => p !== purpose) };
      }
      if (prev.purposes.length >= MAX_SECONDARY_PURPOSES) return prev;
      return { ...prev, purposes: [...prev.purposes, purpose] };
    });
  };

  const toggleStatPreference = (key: string) => {
    setForm((prev) => {
      const active = prev.stat_preferences.includes(key);
      if (active) {
        return { ...prev, stat_preferences: prev.stat_preferences.filter((k) => k !== key) };
      }
      if (prev.stat_preferences.length >= MAX_STAT_PREFS) return prev;
      return { ...prev, stat_preferences: [...prev.stat_preferences, key] };
    });
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.weight_lbs.trim() !== '') {
      const weight = parseFloat(form.weight_lbs);
      if (!/^\d+(\.\d+)?$/.test(form.weight_lbs.trim()) || weight < 500 || weight > 20000) {
        next.weight_lbs = 'Weight must be between 500 and 20,000 lbs';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setGeneralError('');
    if (!validate()) return;

    const input: Partial<BuildProfile> = {
      purpose_primary: (form.purpose_primary || null) as BuildProfile['purpose_primary'],
      purposes: form.purposes,
      build_stage: form.build_stage || null,
      current_goal: form.current_goal.trim() || null,
      summary: form.summary.trim() || null,
      weight_lbs: form.weight_lbs.trim() ? parseFloat(form.weight_lbs) : null,
      weight_source: form.weight_lbs.trim() ? form.weight_source : null,
      accent_color: form.accent_color || null,
      hero_text_placement: form.hero_text_placement || null,
      hero_treatment: form.hero_treatment || null,
      stat_preferences: sanitizeStatPreferences(form.stat_preferences),
      owner_card_enabled: form.owner_card_enabled,
      owner_card: {
        display_name: form.owner_display_name.trim() || undefined,
        bio: form.owner_bio.trim() || undefined,
        location: form.owner_location.trim() || undefined,
        role: form.owner_role.trim() || undefined,
        ownership_since: form.owner_ownership_since || undefined,
      },
    };

    setSaving(true);
    try {
      const saved = await apiService.saveBuildProfile(vehicleId, input);
      showToast('Build profile saved.', 'success');
      onSaved?.(saved);
      if (variant === 'modal') onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save the build profile.';
      const field = fieldForBackendError(message);
      if (field) {
        setErrors((prev) => ({ ...prev, [field]: message }));
      } else {
        setGeneralError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const secondaryChoices = PURPOSES.filter((p) => p !== form.purpose_primary);

  return (
    <EditorChrome
      variant={variant}
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      title="Edit build profile"
      description="Purpose, stage, summary, and how the build page presents this car."
      size="xl"
      closeOnOverlayClick={!saving}
      closeOnEscape={!saving}
      footer={
        <div
          className={`flex gap-2 ${
            variant === 'panel' ? 'flex-col' : 'flex-col-reverse sm:flex-row sm:justify-end'
          }`}
        >
          {variant === 'modal' ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={saving}
              className="min-h-[44px]"
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            form="build-profile-form"
            variant="primary"
            loading={saving}
            loadingLabel="Saving…"
            disabled={loading || Boolean(loadError)}
            fullWidth={variant === 'panel'}
            className={`min-h-[44px] ${variant === 'panel' ? 'justify-center' : ''}`}
          >
            {variant === 'panel' ? 'Save story & look' : 'Save profile'}
          </Button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center gap-3 text-sm text-gray-400 py-8" role="status">
          <span className="spinner-sm" aria-hidden />
          Loading build profile…
        </div>
      )}

      {!loading && loadError && (
        <div
          className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
          role="alert"
        >
          {loadError}
        </div>
      )}

      {!loading && !loadError && (
        <>
          {generalError && (
            <div
              className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
              role="alert"
            >
              {generalError}
            </div>
          )}

          <form id="build-profile-form" onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Purpose */}
            <section aria-label="Build purpose" className="space-y-4">
              <div>
                <label
                  htmlFor="profile-purpose"
                  className="input-label"
                >
                  Primary purpose
                </label>
                <select
                  id="profile-purpose"
                  value={form.purpose_primary}
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      purpose_primary: next,
                      purposes: prev.purposes.filter((p) => p !== next),
                    }));
                  }}
                  className="input min-h-[44px]"
                  disabled={saving}
                  aria-invalid={Boolean(errors.purpose_primary)}
                  aria-describedby={errors.purpose_primary ? 'profile-purpose-error' : undefined}
                >
                  <option value="">Not set</option>
                  {PURPOSES.map((p) => (
                    <option key={p} value={p}>
                      {PURPOSE_LABELS[p]}
                    </option>
                  ))}
                </select>
                {errors.purpose_primary && (
                  <p id="profile-purpose-error" className="mt-1 text-sm text-red-400">
                    {errors.purpose_primary}
                  </p>
                )}
              </div>

              <fieldset className="min-w-0 overflow-x-hidden rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.03] to-transparent p-3 sm:p-4">
                <legend className="sr-only">Secondary purposes</legend>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
                  <p className="input-label !mb-0" id="secondary-purpose-heading">
                    Secondary purposes
                  </p>
                  <p
                    className="text-xs tabular-nums text-gray-500"
                    aria-live="polite"
                    id="secondary-purpose-count"
                  >
                    {form.purposes.length}/{MAX_SECONDARY_PURPOSES} selected
                  </p>
                </div>
                <p className="text-xs text-gray-500 mb-3 max-w-[40ch] break-words" id="secondary-purpose-help">
                  Optional tags that sit beside your primary purpose.
                </p>
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 gap-2 min-w-0"
                  role="group"
                  aria-labelledby="secondary-purpose-heading"
                  aria-describedby="secondary-purpose-help secondary-purpose-count"
                >
                  {secondaryChoices.map((p) => {
                    const active = form.purposes.includes(p);
                    const atLimit = !active && form.purposes.length >= MAX_SECONDARY_PURPOSES;
                    return (
                      <PreferenceToggle
                        key={p}
                        label={PURPOSE_LABELS[p]}
                        hint={PURPOSE_HINTS[p]}
                        pressed={active}
                        disabled={saving || atLimit}
                        onClick={() => toggleSecondaryPurpose(p)}
                      />
                    );
                  })}
                </div>
              </fieldset>
            </section>

            {/* Stage + goal */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="profile-stage"
                  className="input-label"
                >
                  Build stage
                </label>
                <select
                  id="profile-stage"
                  value={form.build_stage}
                  onChange={(e) => setField('build_stage', e.target.value)}
                  className="input min-h-[44px]"
                  disabled={saving}
                  aria-invalid={Boolean(errors.build_stage)}
                  aria-describedby={errors.build_stage ? 'profile-stage-error' : undefined}
                >
                  <option value="">Not set</option>
                  {BUILD_STAGES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {errors.build_stage && (
                  <p id="profile-stage-error" className="mt-1 text-sm text-red-400">
                    {errors.build_stage}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="profile-goal"
                  className="input-label"
                >
                  Current goal
                </label>
                <input
                  type="text"
                  id="profile-goal"
                  value={form.current_goal}
                  onChange={(e) => setField('current_goal', e.target.value)}
                  maxLength={200}
                  className="input min-h-[44px]"
                  placeholder="e.g. Sub 2:00 at the local circuit"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Summary */}
            <div>
              <label
                htmlFor="profile-summary"
                className="input-label"
              >
                Owner summary
              </label>
              <textarea
                id="profile-summary"
                value={form.summary}
                onChange={(e) => setField('summary', e.target.value)}
                maxLength={400}
                rows={3}
                className="input resize-none"
                placeholder="Describe the build in your own words"
                disabled={saving}
                aria-describedby="profile-summary-help"
              />
              <div className="flex justify-between mt-1">
                <p id="profile-summary-help" className="text-xs text-gray-500">
                  Replaces the automatically generated summary on the build page.
                </p>
                <p className="text-xs text-gray-500 tabular-nums" aria-hidden>
                  {form.summary.length}/400
                </p>
              </div>
            </div>

            {/* Weight */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="profile-weight"
                  className="input-label"
                >
                  Weight (lbs)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id="profile-weight"
                  value={form.weight_lbs}
                  onChange={(e) => setField('weight_lbs', e.target.value)}
                  className={`input min-h-[44px] ${errors.weight_lbs ? 'border-red-500' : ''}`}
                  placeholder="e.g. 3120"
                  disabled={saving}
                  aria-invalid={Boolean(errors.weight_lbs)}
                  aria-describedby={errors.weight_lbs ? 'profile-weight-error' : 'profile-weight-help'}
                />
                {errors.weight_lbs ? (
                  <p id="profile-weight-error" className="mt-1 text-sm text-red-400">
                    {errors.weight_lbs}
                  </p>
                ) : (
                  <p id="profile-weight-help" className="mt-1 text-xs text-gray-500">
                    Used for power-to-weight. 500 to 20,000 lbs.
                  </p>
                )}
              </div>
              <fieldset>
                <legend className="input-label">
                  Weight source
                </legend>
                <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="Weight source">
                  {(
                    [
                      { value: 'owner', label: 'Owner estimate' },
                      { value: 'scale', label: 'Corner scales' },
                      { value: 'factory', label: 'Factory spec' },
                    ] as const
                  ).map((opt) => (
                    <label
                      key={opt.value}
                      className="inline-flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-gray-200"
                    >
                      <input
                        type="radio"
                        name="weight_source"
                        value={opt.value}
                        checked={form.weight_source === opt.value}
                        onChange={() => setField('weight_source', opt.value)}
                        className="checkbox-control"
                        disabled={saving}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            {/* Stat preferences - chip toggles (power/torque live in fields above) */}
            <fieldset className="min-w-0 overflow-x-hidden rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.03] to-transparent p-3 sm:p-4">
              <legend className="sr-only">Stats strip preferences</legend>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
                <p className="input-label !mb-0" id="stat-prefs-heading">
                  Stats strip
                </p>
                <p
                  className="text-xs tabular-nums text-gray-500"
                  aria-live="polite"
                  id="stat-prefs-count"
                >
                  {form.stat_preferences.length}/{MAX_STAT_PREFS} selected
                </p>
              </div>
              <p className="text-xs text-gray-500 mb-3 max-w-[40ch] break-words" id="stat-prefs-help">
                Choose what visitors see under power and torque. Empty stats stay hidden.
              </p>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-2 min-w-0"
                role="group"
                aria-labelledby="stat-prefs-heading"
                aria-describedby="stat-prefs-help stat-prefs-count"
              >
                {STAT_OPTIONS.map((stat) => {
                  const checked = form.stat_preferences.includes(stat.key);
                  const atLimit = !checked && form.stat_preferences.length >= MAX_STAT_PREFS;
                  return (
                    <PreferenceToggle
                      key={stat.key}
                      label={stat.label}
                      hint={stat.hint}
                      pressed={checked}
                      disabled={saving || atLimit}
                      onClick={() => toggleStatPreference(stat.key)}
                    />
                  );
                })}
              </div>
            </fieldset>

            {/* Customization */}
            <section aria-label="Page customization" className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-200">Page customization</h3>

              <fieldset>
                <legend className="input-label">
                  Accent color
                </legend>
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-describedby={errors.accent_color ? 'accent-error' : undefined}
                >
                  <button
                    type="button"
                    onClick={() => setField('accent_color', '')}
                    disabled={saving}
                    aria-pressed={form.accent_color === ''}
                    className={`min-h-[44px] px-3 rounded-lg border text-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                      form.accent_color === ''
                        ? 'border-orange-500/60 text-white bg-[#141414]'
                        : 'border-white/[0.08] text-gray-300 hover:border-white/[0.18]'
                    }`}
                  >
                    Default
                  </button>
                  {ACCENT_PALETTE.map((color) => {
                    const active = form.accent_color === color;
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setField('accent_color', color)}
                        disabled={saving}
                        aria-pressed={active}
                        className={`min-h-[44px] px-3 rounded-lg border text-sm inline-flex items-center gap-2 capitalize transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                          active
                            ? 'border-orange-500/60 text-white bg-[#141414]'
                            : 'border-white/[0.08] text-gray-300 hover:border-white/[0.18]'
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full ${ACCENT_SWATCH_CLASSES[color] ?? 'bg-[#6b6b6b]'}`}
                          aria-hidden
                        />
                        {color}
                      </button>
                    );
                  })}
                </div>
                {errors.accent_color && (
                  <p id="accent-error" className="mt-1 text-sm text-red-400">
                    {errors.accent_color}
                  </p>
                )}
              </fieldset>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="profile-hero-placement"
                    className="input-label"
                  >
                    Hero text placement
                  </label>
                  <select
                    id="profile-hero-placement"
                    value={form.hero_text_placement}
                    onChange={(e) => setField('hero_text_placement', e.target.value)}
                    className="input min-h-[44px]"
                    disabled={saving}
                    aria-invalid={Boolean(errors.hero_text_placement)}
                    aria-describedby={errors.hero_text_placement ? 'hero-placement-error' : undefined}
                  >
                    <option value="">Default</option>
                    {HERO_PLACEMENTS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  {errors.hero_text_placement && (
                    <p id="hero-placement-error" className="mt-1 text-sm text-red-400">
                      {errors.hero_text_placement}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="profile-hero-treatment"
                    className="input-label"
                  >
                    Hero treatment
                  </label>
                  <select
                    id="profile-hero-treatment"
                    value={form.hero_treatment}
                    onChange={(e) => setField('hero_treatment', e.target.value)}
                    className="input min-h-[44px]"
                    disabled={saving}
                    aria-invalid={Boolean(errors.hero_treatment)}
                    aria-describedby={errors.hero_treatment ? 'hero-treatment-error' : undefined}
                  >
                    <option value="">Default</option>
                    {HERO_TREATMENTS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  {errors.hero_treatment && (
                    <p id="hero-treatment-error" className="mt-1 text-sm text-red-400">
                      {errors.hero_treatment}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Owner card */}
            <section aria-label="Owner card" className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-200">Owner card</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    An optional card about you on the build page.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={form.owner_card_enabled}
                    onChange={(e) => setField('owner_card_enabled', e.target.checked)}
                    className="checkbox-control"
                    disabled={saving}
                  />
                  Show owner card
                </label>
              </div>

              {form.owner_card_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="owner-display-name"
                      className="input-label"
                    >
                      Display name
                    </label>
                    <input
                      type="text"
                      id="owner-display-name"
                      value={form.owner_display_name}
                      onChange={(e) => setField('owner_display_name', e.target.value)}
                      maxLength={120}
                      className="input min-h-[44px]"
                      placeholder="How you want to be credited"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="owner-role"
                      className="input-label"
                    >
                      Role
                    </label>
                    <input
                      type="text"
                      id="owner-role"
                      value={form.owner_role}
                      onChange={(e) => setField('owner_role', e.target.value)}
                      maxLength={60}
                      className="input min-h-[44px]"
                      placeholder="e.g. Owner and builder"
                      disabled={saving}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="owner-bio"
                      className="input-label"
                    >
                      Short bio
                    </label>
                    <textarea
                      id="owner-bio"
                      value={form.owner_bio}
                      onChange={(e) => setField('owner_bio', e.target.value)}
                      maxLength={280}
                      rows={2}
                      className="input resize-none"
                      placeholder="A sentence or two about you"
                      disabled={saving}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right tabular-nums" aria-hidden>
                      {form.owner_bio.length}/280
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="owner-location"
                      className="input-label"
                    >
                      Location
                    </label>
                    <input
                      type="text"
                      id="owner-location"
                      value={form.owner_location}
                      onChange={(e) => setField('owner_location', e.target.value)}
                      maxLength={80}
                      className="input min-h-[44px]"
                      placeholder="e.g. Pacific Northwest"
                      disabled={saving}
                      aria-describedby="owner-location-help"
                    />
                    <p id="owner-location-help" className="mt-1 text-xs text-gray-500">
                      City or region only, never exact address.
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="owner-since"
                      className="input-label"
                    >
                      Ownership start
                    </label>
                    <input
                      type="month"
                      id="owner-since"
                      value={form.owner_ownership_since}
                      onChange={(e) => setField('owner_ownership_since', e.target.value)}
                      className="input min-h-[44px]"
                      disabled={saving}
                    />
                  </div>
                </div>
              )}
            </section>
          </form>
        </>
      )}
    </EditorChrome>
  );
};

export default BuildProfileEditorModal;
